<template>
    <VJoyPanel class="content-panel" :class="{isMobileScreen}" :data-type="action.actionType" :data-testid="`pending-action-card-${index}`">
        <template #panel-title>
            <div class="content-panel_title">
                <VJoyIcon size="small" :name="icon" color="primary" />
                <h2 data-testid="pending-action-card-title">
                    <slot
                        name="title"
                        data-testid="pending-action-card-title"
                        :freelancer-name="getFreelancerFirstName"
                        :month="getTranslatedMonth"
                        :due-date="formatedDueDate"
                    />
                </h2>
            </div>
        </template>
        <template #panel-title-action>
            <span class="content-panel_date" data-testid="pending-action-card-due-date">
                {{ formatedDueDate }}
            </span>
        </template>
        <template #panel-body>
            <div class="content-panel_body" data-testid="pending-action-card-content">
                <slot name="body" :freelancer-name="getFreelancerFirstName" :month="getTranslatedMonth" :due-date="formatedDueDate" />
            </div>
        </template>
        <template #panel-action>
            <div class="content-panel_actions" data-testid="pending-action-card-actions">
                <slot name="actions" />
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isSameDay, isToday, subDays} from 'date-fns';
    import capitalize from 'lodash-es/capitalize';
    import {storeToRefs} from 'pinia';
    import {useFormat, useTranslation} from '#imports';
    import {VJoyIcon, VJoyPanel} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import type {ActivityPendingActionByType} from '../../types';
    import {useProjectInformationStore, useScreenSizeStore} from '@/common/stores';

    const props = defineProps<{
        icon: JoyIconsId;
        action: ActivityPendingActionByType<any>;
        index: number;
    }>();

    const {formatDate} = useFormat();
    const {t} = useTranslation();

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen} = storeToRefs(screenSizeStore);

    const getTranslatedMonth = computed<string>(() => {
        if (props.action.extraData && 'month' in props.action.extraData && props.action.extraData?.month) {
            return capitalize(formatDate(new Date(2000, parseInt(props.action.extraData?.month) - 1), {month: 'long'}));
        }
        return '-';
    });

    const getFreelancerFirstName = computed<string>(() => projectInformation.value.freelancer.firstName);

    const formatedDueDate = computed<string>(() => {
        if (isToday(props.action.dueDate)) {
            return t('generic.date.today');
        } else if (isSameDay(subDays(new Date(), 1), props.action.dueDate)) {
            return t('generic.date.yesterday');
        }
        return formatDate(props.action.dueDate, {day: 'numeric', month: 'long'});
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/_text.scss' as fonts;

    .content-panel {
        flex-shrink: 1;

        &_title {
            display: flex;
            flex-flow: row nowrap;
            align-items: flex-start;
            align-content: center;

            .joy-icon {
                flex: 0 0 auto;
                line-height: 1.2;
                margin-top: 1px;
                margin-right: var(--joy-core-spacing-3);
            }

            :deep(i.icon) {
                color: var(--joy-color-primary-50) !important;
            }

            h2 {
                line-height: 1.2;
            }
        }

        &_date {
            vertical-align: middle;

            @include fonts.getFontBySize(primary-200);
        }

        :deep(.joy-panel__body) {
            display: flex;
        }

        &_body {
            flex: 1;
            width: 500px;
            text-align: left;

            @include fonts.getFontBySize(primary-300);
        }

        &_actions {
            display: flex;
            justify-content: flex-end;
            margin-top: var(--joy-core-spacing-4);
        }
    }

    .isMobileScreen {
        :deep(.joy-panel__footer) {
            .joy-button {
                width: 100%;
            }
        }

        .content-panel {
            &_actions {
                flex-direction: column;
                gap: var(--joy-core-spacing-3);
            }
        }
    }

    :deep(.joy-panel__header) {
        display: flex;
        align-items: flex-start;
        font-size: var(--joy-font-size-primary-300);

        .joy-panel__header-action {
            color: var(--joy-color-neutral-40);
        }
    }
</style>
